import { Icon, IconDefinitions, SectionHeader, InfoSection, InfoDetail } from "../../../../components";
import { ActiveConfiguration, HeartbeatModel, HeartbeatPayloadDetailed } from "../../../../data/entities";
import { getRelativeTime, formatDateTime } from "../../../../app/dateHelpers";
import { getKnownInstalledCommands } from "../../helper/cteye-commands";

interface HeartbeatDetailsPanelProps {
  heartbeat: HeartbeatModel | null;
}

export function HeartbeatDetailsPanel({ heartbeat }: HeartbeatDetailsPanelProps) {
  if (!heartbeat) {
    return (
      <div className="text-center text-muted py-5">
        <Icon iconName={IconDefinitions.circleInfo} className="mb-2" />
        <p className="mb-0">No heartbeat data available</p>
      </div>
    );
  }

  const payload = heartbeat.payload as HeartbeatPayloadDetailed;
  const availableFiles = payload?.hostInfo?.filteredDataFiles || [];
  const installedCommands = payload?.installedCommands || [];
  const activeConfig: ActiveConfiguration | null = payload?.activeConfiguration ?? null;
 
  const heartbeatInfo: InfoDetail[] = [
    {
      label: "Last Heartbeat",
      value: getRelativeTime(new Date(heartbeat.modifiedDate)),
      icon: <Icon iconName={IconDefinitions.circleCheck} />,
    },
    {
      label: "Created Date",
      value: formatDateTime(new Date(heartbeat.createdDate)),
      icon: <Icon iconName={IconDefinitions.chartLineUp} />,
    },
    ...(payload?.hostInfo?.ctEyeVersion
      ? [{
          label: "CTEye Version",
          value: payload.hostInfo.ctEyeVersion,
          icon: <Icon iconName={IconDefinitions.CTEye} />,
        }]
      : []),
  ];

  const activeConfigInfo: InfoDetail[] | null = activeConfig
    ? [
        {
          label: "Heartbeat Interval",
          value: `${activeConfig.heartbeatIntervalSeconds}s`,
          icon: <Icon iconName={IconDefinitions.heartbeat} />,
        },
        {
          label: "Target Import Rows",
          value: `${activeConfig.targetImportRows}`,
          icon: <Icon iconName={IconDefinitions.hashtag} />,
        },
        ...(activeConfig.rollingAverageWindowSize != null
          ? [{
              label: "Rolling Avg Window",
              value: `${activeConfig.rollingAverageWindowSize} data points`,
              icon: <Icon iconName={IconDefinitions.chartLineUp} />,
            }]
          : []),
      ]
    : null;

  const knownCommands = getKnownInstalledCommands(installedCommands);

  return (
    <>
      <InfoSection title="Heartbeat Information" details={heartbeatInfo} />
      {activeConfigInfo && (
        <InfoSection title="Active Configuration" details={activeConfigInfo} />
      )}

      {/* Available Files */}
      <div className="mb-3">
        <SectionHeader title={`Available Data Files (${availableFiles.length})`} />
        <div className="px-3 pt-3">
          {availableFiles.length === 0 ? (
            <p className="text-muted mb-0">No data files detected</p>
          ) : (
            <div className="list-group list-group-flush">
              {availableFiles.map((file, idx) => (
                <div key={idx} className="list-group-item px-0 py-2">
                  <div className="d-flex align-items-start justify-content-between gap-2">
                    <div className="d-flex align-items-start flex-grow-1">
                      <Icon iconName={IconDefinitions.fileAlt} className="me-2 text-muted mt-1" />
                      <div className="flex-grow-1" style={{ minWidth: 0 }}>
                        <code className="small text-break d-block">{file.path}</code>
                        <small className="text-muted">
                          {(file.sizeBytes / 1024 / 1024).toFixed(2)} MB
                        </small>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Installed Commands */}
      <div className="mb-0">
        <SectionHeader title={`Installed Commands (${knownCommands.length})`} />
        <div className="px-3 pt-3">
          {knownCommands.length === 0 ? (
            <p className="text-muted mb-0">No recognised commands installed</p>
          ) : (
            <div className="list-group list-group-flush">
              {knownCommands.map((cmd) => (
                <div key={cmd.name} className="list-group-item px-0 py-2">
                  <div className="d-flex align-items-start gap-2">
                    <Icon iconName={cmd.icon} className="text-muted mt-1" />
                    <div>
                      <div className="small fw-semibold">{cmd.displayName}</div>
                      <div className="small text-muted">{cmd.description}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}